import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { sessionsApi, resourcesApi } from '../services/api';
import {
  Search,
  X as TerminateIcon,
  Clock,
  User,
  Users,
  Circle,
  Play,
  Eye,
  Pencil,
  History,
  AlertTriangle,
  SearchX,
} from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import { sessionTone } from '@/lib/accessDecision';
import { Button, Card, EmptyState, IconButton, MonoCell, PageHeader, SearchField, Select, OutcomeBadge, PaginationBar, RecordingIndicator, StatCard, TanstackTable, Timestamp, TimeRangePicker } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import StatValue from '@/components/StatValue';
import { ResourceTypeTile } from '@/lib/resourceIcons';
import { isSessionExpired, isSessionExpiringSoon, isSessionLive } from '@/lib/sessionStatus';
import { errorMessage } from '@/lib/http';
import { toast } from '@/lib/toast';
import { useUrlState, useUrlTimeRange } from '@/lib/useUrlState';
import { resolveRange, type TimeRange } from '@/lib/timeRange';
import { filterToWindow, windowPhrase } from '@/features/audit/timeWindow';
import { rememberSessionsSearch, type PlayerLinkState } from '@/features/sessions/returnTo';
import { fetchUserFilterOptions, userFilterQuery } from '../lib/userFilterOptions';
import { usePermissions } from '../hooks/usePermissions';
import { User as IAMUser } from '../types/iam';
import type { Session } from '../types';

// A session row as the API returns it: the shared type plus the recording link.
type SessionRow = Session & { recording_url?: string | null };

const columnHelper = createColumnHelper<SessionRow>();

const isDesktopType = (type?: string) => type === 'desktop' || type === 'rdp';

// Parses a timestamp, or null when it is missing or unparseable.
const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dateSort = (value: string | null | undefined): number | null => parseDate(value)?.getTime() ?? null;

// Status as the table draws it, for sorting: expired / expiring override the
// stored status the same way the badge does.
const statusSort = (s: SessionRow): string =>
  isSessionExpired(s) ? 'expired' : isSessionExpiringSoon(s) ? 'expiring' : String(s.status ?? '');

interface SessionsTableProps {
  sessions: SessionRow[];
  isLoading: boolean;
  getResourceName: (resourceId?: string) => string | null;
  getSessionTypeIcon: (type: string) => React.ReactNode;
  handleTerminate: (session: SessionRow) => void;
  terminateMutationLoading: boolean;
  onPlayRecording?: (session: SessionRow) => void;
  /** Open the live session: read-only (watch) or typing into it (join as peer). */
  onOpenLive?: (session: SessionRow, mode: 'observer' | 'peer') => void;
  /** May join as a peer (type into the session). */
  canJoin?: boolean;
  searchTerm: string;
  /** The time range hides some of this page's rows. */
  narrowed: boolean;
}

const SessionsTable: React.FC<SessionsTableProps> = ({
  sessions,
  isLoading,
  getResourceName,
  getSessionTypeIcon,
  handleTerminate,
  terminateMutationLoading,
  onPlayRecording,
  onOpenLive,
  canJoin = false,
  searchTerm,
  narrowed,
}) => {
  const columns = useMemo<ColumnDef<SessionRow, any>[]>(
    () => [
      columnHelper.display({
        id: 'user',
        header: 'User',
        meta: { sortable: true, sortValue: (s: SessionRow) => String(s.user_email || s.user_id || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10">
              <div className="h-10 w-10 rounded-full bg-[var(--d2d-border)] flex items-center justify-center">
                <User className="h-5 w-5 text-[var(--d2d-text-secondary)]" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-dense font-medium text-[var(--d2d-text-primary)]">
                {row.original.user_email || row.original.user_id}
              </div>
              <div className="flex items-center gap-1 text-dense text-[var(--d2d-text-muted)]">
                ID <MonoCell value={row.original.id} width="10ch" />
              </div>
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'resource',
        header: 'Resource',
        meta: { sortable: true, sortValue: (s: SessionRow) => String(s.resource_name || getResourceName(s.resource_id) || s.resource_id || '').toLowerCase() },
        cell: ({ row }) => {
          const name = row.original.resource_name || getResourceName(row.original.resource_id);
          return name ? (
            <span className="text-dense text-[var(--d2d-text-primary)]">{name}</span>
          ) : row.original.resource_id ? (
            <MonoCell value={row.original.resource_id} width="12ch" />
          ) : (
            <span className="text-dense text-[var(--d2d-text-muted)]">—</span>
          );
        },
      }),
      columnHelper.display({
        id: 'type',
        header: 'Type',
        meta: { sortable: true, sortValue: (s: SessionRow) => String(s.session_type ?? '') },
        cell: ({ row }) => (
          <div className="flex items-center">
            <span className="mr-2">{getSessionTypeIcon(row.original.session_type)}</span>
            <span className="text-dense text-[var(--d2d-text-primary)] capitalize">
              {row.original.session_type}
            </span>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        meta: { sortable: true, sortValue: statusSort },
        cell: ({ row }) => {
          const isExpired = isSessionExpired(row.original);
          const isExpiringSoon = isSessionExpiringSoon(row.original);
          return (
            <OutcomeBadge
              value={isExpired ? 'expired' : isExpiringSoon ? 'expiring' : row.original.status}
              tone={isExpired || isExpiringSoon ? undefined : sessionTone(row.original.status)}
              className="capitalize"
            >
              {isExpired ? 'Expired' : row.original.status}
            </OutcomeBadge>
          );
        },
      }),
      columnHelper.accessor('started_at', {
        header: 'Started',
        meta: { sortable: true, sortValue: (s: SessionRow) => dateSort(s.started_at) },
        cell: (info) => (
          <div>
            <div className="text-[var(--d2d-text-primary)]"><Timestamp value={info.getValue()} noSeconds /></div>
            <div className="text-xs text-[var(--d2d-text-muted)]">
              <Timestamp value={info.getValue()} format="relative" />
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'expires',
        header: 'Expires',
        // "Never" sorts after every real expiry.
        meta: { sortable: true, sortValue: (s: SessionRow) => dateSort(s.expires_at) ?? Number.MAX_SAFE_INTEGER },
        cell: ({ row }) => {
          const expiresAt = parseDate(row.original.expires_at);
          const isExpired = expiresAt ? expiresAt < new Date() : false;
          return (
            <div>
              <div className="text-[var(--d2d-text-primary)]">{expiresAt ? <Timestamp value={expiresAt} noSeconds /> : 'Never'}</div>
              <div className="text-xs text-[var(--d2d-text-muted)]">
                {!expiresAt ? '—' : isExpired ? 'Expired' : <Timestamp value={expiresAt} format="relative" />}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'recording',
        header: 'Recording',
        cell: ({ row }) => {
          const isActive = isSessionLive(row.original);
          const hasRecording = !!row.original.recording_url;
          return (
            <div className="flex items-center">
              {isActive && (
                <RecordingIndicator />
              )}
              {hasRecording && !isActive && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onPlayRecording?.(row.original)}
                  leadingIcon={<Play className="h-3 w-3 fill-current" />}
                  aria-label="Play Recording"
                >
                  Play
                </Button>
              )}
              {!isActive && !hasRecording && (
                <span className="text-xs text-[var(--d2d-text-muted)]">&mdash;</span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="text-right block">Actions</span>,
        cell: ({ row }) => {
          return (
            <div className="flex items-center justify-end gap-1">
              {/* Live terminal sessions: watch read-only, or join as a peer (design
                  eval F19). The join socket streams terminal output, so a desktop
                  session has no Watch here. */}
              {isSessionLive(row.original) && onOpenLive && !isDesktopType(row.original.session_type) && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<Eye className="h-3 w-3" />}
                    onClick={() => onOpenLive(row.original, 'observer')}
                    aria-label={`Watch ${row.original.user_email || row.original.user_id}'s session (read-only)`}
                  >
                    Watch
                  </Button>
                  {canJoin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<Pencil className="h-3 w-3" />}
                      onClick={() => onOpenLive(row.original, 'peer')}
                      aria-label={`Join ${row.original.user_email || row.original.user_id}'s session as a peer (you can type)`}
                    >
                      Join
                    </Button>
                  )}
                </>
              )}
              {isSessionLive(row.original) && (
                <IconButton
                  size="sm"
                  aria-label="Terminate Session"
                  tooltip="Terminate Session"
                  onClick={() => handleTerminate(row.original)}
                  className="text-[var(--danger-text)]"
                  disabled={terminateMutationLoading}
                  icon={<TerminateIcon className="h-4 w-4" />}
                />
              )}
              {row.original.terminated_at && (
                <div className="text-xs text-[var(--d2d-text-muted)]">
                  Terminated <Timestamp value={row.original.terminated_at} noSeconds />
                </div>
              )}
            </div>
          );
        },
      }),
    ],
    [getResourceName, getSessionTypeIcon, handleTerminate, terminateMutationLoading, onPlayRecording, onOpenLive, canJoin]
  );

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <TanstackTable
          table={table}
          aria-label="Sessions"
          loading={isLoading}
          empty={
            searchTerm || narrowed ? (
              <EmptyState
                icon={<SearchX />}
                title={searchTerm ? `No sessions on this page match "${searchTerm}"` : 'No sessions on this page in this time range'}
                description="Search and the time range apply to the sessions on this page. Try another page, a different term or a wider range."
              />
            ) : (
              <EmptyState
                icon={<History />}
                title="No sessions found"
                description="Sessions will appear here once users connect to resources."
              />
            )
          }
        />
      </div>
    </Card>
  );
};

// Filters, search, paging and the time range live in the URL (design eval
// F24), so the player's Back and a refresh return to the same view.
const URL_DEFAULTS = { q: '', user: '', page: '1', limit: '20' };
// Sessions opens on everything, as before; the time range narrows it.
const ALL_TIME: TimeRange = { kind: 'all' };

export default function Sessions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const [range, setRange] = useUrlTimeRange(ALL_TIME, ['page']);
  const searchTerm = url.q;
  const selectedUser = url.user;
  const itemsPerPage = Math.max(1, Number(url.limit) || 20);
  const page = Math.max(0, (Number(url.page) || 1) - 1);

  // The player's Back link returns here, to this exact query.
  useEffect(() => {
    rememberSessionsSearch(location.search);
  }, [location.search]);

  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  // Typing into someone else's session is an admin action in the console;
  // the join endpoint must enforce it too.
  const canJoin = usePermissions().isAdmin();

  // GET /sessions filters by user and pages by limit/offset; it has no search
  // or time parameters, so those two narrow the page that was loaded.
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery(
    ['sessions', selectedUser, page, itemsPerPage],
    () => sessionsApi.list(selectedUser || undefined, itemsPerPage, page * itemsPerPage),
    { keepPreviousData: true }
  );

  const { data: resourcesData } = useQuery('resources-for-sessions', () =>
    resourcesApi.listByRole(undefined, 100, 0)
  );

  // User filter options: the directory, not just whoever is on this page
  // (with a user selected, the page holds only that user).
  const { data: usersData } = useQuery(userFilterQuery, fetchUserFilterOptions);
  const userOptions = useMemo(() => {
    const list: IAMUser[] = Array.isArray(usersData) ? usersData : usersData?.data || [];
    const opts = list.map((u) => ({ id: u.id, label: u.email || u.name || u.id }));
    if (selectedUser && !opts.some((o) => o.id === selectedUser)) opts.unshift({ id: selectedUser, label: selectedUser });
    return opts;
  }, [usersData, selectedUser]);

  const terminateMutation = useMutation(sessionsApi.terminate, {
    onSuccess: () => {
      queryClient.invalidateQueries('sessions');
      toast.success('Session terminated');
    },
    onError: (err) => {
      toast.error(errorMessage(err, 'Failed to terminate session'));
    },
  });

  const getResourceName = (resourceId?: string): string | null => {
    if (!resourceId) return null;
    const resource = resourcesData?.data.resources?.find(r => r.id === resourceId);
    return resource?.external_id || null;
  };

  const handleTerminate = async (session: SessionRow) => {
    const who = session.user_email || session.user_id || 'this user';
    const what = session.resource_name || getResourceName(session.resource_id) || session.resource_id || 'this resource';
    if (await confirm({
      title: 'Terminate session?',
      message: `Terminate ${who}'s ${session.session_type ?? ''} session on ${what} (ID ${String(session.id).slice(0, 8)})? They are disconnected immediately.`.replace(/\s+/g, ' '),
      confirmLabel: 'Terminate',
      variant: 'danger',
    })) {
      // Errors are reported by onError; mutate (not mutateAsync) so a failure
      // does not surface as an unhandled rejection.
      terminateMutation.mutate(session.id);
    }
  };

  const pageRows = data?.data.sessions || [];
  // Resolved per fetch, so a relative range moves with each refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bounds = useMemo(() => resolveRange(range), [range, dataUpdatedAt]);
  const inWindow = filterToWindow(pageRows, (s) => s.started_at, bounds);
  const needle = searchTerm.toLowerCase();
  const filteredSessions = inWindow.filter(session =>
    !needle ||
    session.user_id.toLowerCase().includes(needle) ||
    (session.user_email || '').toLowerCase().includes(needle) ||
    session.session_type.toLowerCase().includes(needle)
  );

  const getSessionTypeIcon = (type: string) => <ResourceTypeTile type={type} size={28} />;

  const uniqueUsers = new Set(filteredSessions.map(s => s.user_id));
  const narrowedByRange = inWindow.length < pageRows.length;
  const shownCaption = `of ${filteredSessions.length} shown on this page`;
  const windowText = windowPhrase(range);

  return (
    <div>
      <PageHeader
        className="mb-8"
        icon={<History />}
        title="Sessions"
        description="Monitor and manage user sessions across all resources"
        timeRange={<TimeRangePicker value={range} onChange={setRange} label="Session start time range" allowAll />}
        onRefresh={() => refetch()}
        refreshing={isFetching}
      />

      {/* Filters */}
      <Card className="p-4 mb-6 overflow-visible">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchField
              label="Search this page by user or type"
              placeholder="Search this page..."
              value={searchTerm}
              onChange={(value) => setUrl({ q: value }, { replace: true })}
            />
          </div>
          <div className="sm:w-56">
            <Select
              className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
              label={<span className="sr-only">User</span>}
              value={selectedUser}
              onChange={(e) => setUrl({ user: e.target.value, page: null })}
            >
              <option value="">All Users</option>
              {userOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        {(searchTerm || range.kind !== 'all') && (
          <p className="mt-3 text-sm text-[var(--d2d-text-secondary)]">
            Search{range.kind !== 'all' ? ` and the time range (sessions started ${windowText})` : ''} apply to the {pageRows.length} sessions on this page: the sessions API filters only by user.
          </p>
        )}
      </Card>

      {/* Sessions Stats. The first is the server's count; the rest count the
          rows shown on this page, and say so (design eval F7). */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<User />}
          label="Total Sessions"
          value={<StatValue value={data?.data.total || 0} caption={selectedUser ? 'for this user, all time' : 'all users, all time'} />}
        />
        <StatCard
          icon={<Circle className="fill-current" />}
          accent="success"
          label="Active"
          value={<StatValue value={filteredSessions.filter(s => isSessionLive(s)).length} caption={shownCaption} />}
        />
        <StatCard
          icon={<Clock />}
          accent="warning"
          label="Expiring Soon"
          value={<StatValue value={filteredSessions.filter(s => isSessionExpiringSoon(s)).length} caption={shownCaption} />}
        />
        <StatCard
          icon={<Users />}
          label="Unique Users"
          value={<StatValue value={uniqueUsers.size} caption={`in the ${filteredSessions.length} shown on this page`} />}
        />
      </div>

      {/* Sessions Table */}
      {error ? (
        <Card className="overflow-hidden">
          <EmptyState
            icon={<AlertTriangle />}
            title="Failed to load sessions"
            description="Something went wrong while loading sessions."
            action={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}
          />
        </Card>
      ) : (
      <SessionsTable
        sessions={filteredSessions}
        isLoading={isLoading}
        getResourceName={getResourceName}
        getSessionTypeIcon={getSessionTypeIcon}
        handleTerminate={handleTerminate}
        terminateMutationLoading={terminateMutation.isLoading}
        onPlayRecording={(session) => navigate(`/sessions/${session.id}/player`, { state: { fromSessions: true } satisfies PlayerLinkState })}
        onOpenLive={(session, mode) => navigate(`/sessions/${encodeURIComponent(session.id)}/join${mode === 'peer' ? '?mode=peer' : ''}`)}
        canJoin={canJoin}
        searchTerm={searchTerm}
        narrowed={narrowedByRange}
      />
      )}

      {/* Pagination */}
      {data && data.data.total > 0 && (
        <div className='mt-4'>
          {data.data.total > 0 && <PaginationBar
            page={page + 1}
            totalRows={data.data.total}
            rowsPerPage={itemsPerPage}
            onPageChange={(p) => setUrl({ page: p })}
            onRowsPerPageChange={(newItemsPerPage) => setUrl({ limit: newItemsPerPage, page: null })}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />}
        </div>
      )}
    </div>
  );
}
